/**
 * Service to validate check-in descriptions
 * This would typically make an API call to validate the format
 * For now, we're implementing a simple validation logic
 */

import { BC_AGENTS_URL } from '../../../constants/constants';

export interface ValidationResult {
  isValid: boolean;
  errorMessages: string[];
}
export type TRawAssessmentResult = {
  grammatically_correct: boolean;
  should_contain_details_of_action_performed: boolean;
  assessment_reasoning: string;
  assessment_result: string;
  suggested_rewritten_check_in: string;
  update_text: string;
  starts_with_capital_letter: boolean;
  starts_with_past_tense_of_action_verb: boolean;
  does_not_contain_bullet_formatting: boolean;
  passed_assessment: boolean;
};
/**
 * Validates if the check-in description follows the standard format
 * @param description - The description to validate
 * @returns Promise that resolves to ValidationResult with validity and error messages
 */
export const validateDescription = async (
  description: string,
): Promise<TRawAssessmentResult> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 seconds timeout

  try {
    const response = await fetch(`${BC_AGENTS_URL}/assess-check-ins`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ description }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(
        `Facing issues from the agent, please have a self-check and submit the check-in again`,
      );
    }

    const data = (await response.json()) as {
      assessment: TRawAssessmentResult;
    };
    return data.assessment;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out after 60 seconds');
    }
    throw new Error(
      `Failed to validate description: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`,
    );
  }
};
